import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { recipientSharesQueryKey } from './useRecipientShares';
import { outreachUpdatesQueryKey } from './useOutreachUpdates';

export interface AnalyticsView {
  id: string;
  outreach_item_id: string;
  name: string;
  description: string | null;
  filters_json: Record<string, any>;
  sections_json: {
    overview_stats?: boolean;
    summary?: boolean;
    charts?: boolean;
    responses_table?: boolean;
    response_columns?: string[];
    /** Viewer downloads on shared pages; absent = allowed, false = disabled. */
    allow_export?: boolean;
  };
  summary_html: string | null;
  is_default_public: boolean;
  share_token: string;
  is_active: boolean;
  expires_at: string | null;
  view_count: number;
  last_viewed_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  chart_count?: number;
}

interface CreateViewParams {
  name: string;
  description?: string;
  filtersJson?: Record<string, any>;
  sectionsJson?: Record<string, boolean | string[]>;
  summaryHtml?: string;
  isDefaultPublic?: boolean;
  expiresInDays?: number;
}

interface UpdateViewParams extends Partial<CreateViewParams> {
  viewId: string;
}

export function useAnalyticsViews(itemId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['analytics-views', itemId];

  const { data, isLoading, error } = useQuery(
    queryKey,
    async () => {
      const response = await api.get(`/outreach/${itemId}/analytics/views`);
      return response.data.data.views as AnalyticsView[];
    },
    {
      enabled: !!itemId,
      staleTime: 30 * 1000
    }
  );

  const createMutation = useMutation(
    async (params: CreateViewParams) => {
      const response = await api.post(`/outreach/${itemId}/analytics/views`, params);
      return response.data.data.view as AnalyticsView;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        toast.success('Report created');
      },
      onError: () => { toast.error('Failed to create report'); }
    }
  );

  const updateMutation = useMutation(
    async ({ viewId, ...params }: UpdateViewParams) => {
      const response = await api.put(`/outreach/${itemId}/analytics/views/${viewId}`, params);
      return response.data.data.view as AnalyticsView;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        toast.success('Report updated');
      },
      onError: () => { toast.error('Failed to update report'); }
    }
  );

  const deleteMutation = useMutation(
    async (viewId: string) => {
      await api.delete(`/outreach/${itemId}/analytics/views/${viewId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        // Shares cascade-delete with their report and updates lose a target, so
        // the Reports & Communications ledger must not keep serving the stale
        // cached rows (a phantom "(deleted report)" group, an old target name).
        queryClient.invalidateQueries(recipientSharesQueryKey(itemId));
        queryClient.invalidateQueries(outreachUpdatesQueryKey(itemId));
        toast.success('Report deleted');
      },
      onError: () => { toast.error('Failed to delete report'); }
    }
  );

  const copyMutation = useMutation(
    async (viewId: string) => {
      const response = await api.post(`/outreach/${itemId}/analytics/views/${viewId}/copy`);
      return response.data.data.view as AnalyticsView;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        toast.success('Report copied');
      },
      onError: () => { toast.error('Failed to copy report'); }
    }
  );

  return {
    views: data || [],
    isLoading,
    error,
    createView: createMutation.mutateAsync,
    updateView: updateMutation.mutateAsync,
    deleteView: deleteMutation.mutate,
    copyView: copyMutation.mutateAsync,
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
    isCopying: copyMutation.isLoading
  };
}
